/**
 * [CUSTOM FILE] — not part of Stretch base theme
 * sticky-buy-button.js
 * --------------------
 * Enhancer for the base sticky buy button section.
 * Adds: colour/size overlays, size persistence across colour-linked product
 * navigation, and overlay closing whenever the bar hides. Showing/hiding the bar
 * itself is the base theme's job (SandwichVisibility -> .is-visible), CSS-driven.
 */

// Wrapped in a block-scoped IIFE (not re-indented) so the script can safely execute more than
// once — the theme editor re-runs it on section reload — without redeclaring the class.
(() => {
if (window.__sbbInit) { window.__sbbInit(); return; }

class StickyBuyBarEnhancer {
  constructor(el) {
    this._el = el;
    this._productId = el.dataset.productId;
    this._controller = new AbortController();

    const dataEl = el.querySelector('.sbb__data');
    if (!dataEl) return;
    this._data = JSON.parse(dataEl.textContent);

    this._backdrop   = document.querySelector('[data-sbb-backdrop]');
    this._sizeValueEl = el.querySelector('[data-sbb-size-value]');

    this._initVariantListener();
    this._initTriggers();
    this._initSizeButtons();
    this._initClose();
    this._initColorLinks();
    this._initVisibilityObserver();
    // [CUSTOM] 2026-10-30: _initFooterObserver() and _initDock() removed. Mobile now uses the same
    // base-theme trigger as desktop (SandwichVisibility -> .is-visible); see CS_CUSTOM_REGISTRY.md.
    this._restoreSizeSelection();
  }

  // ─── Variant listener ────────────────────────────────────────────────────────
  // Only updates the size label and size-button selected states.
  // Button label, disabled state, and price are handled automatically
  // by <product-rerender> fetching fresh section HTML on every variant change.

  _initVariantListener() {
    const variantPicker = document.querySelector(
      `variant-picker[handle="${this._data.productHandle}"]`
    );
    if (!variantPicker) return;

    const formId = variantPicker.getAttribute('form-id');
    if (!formId) return;

    this._formId     = formId;
    this._productForm = document.getElementById(formId);
    if (!this._productForm) return;

    this._productForm.addEventListener(
      'variant:change',
      (e) => this._onVariantChange(e.detail.variant),
      { signal: this._controller.signal }
    );
  }

  _onVariantChange(variant) {
    if (!variant || !Array.isArray(variant.options)) return;

    // Sync all size value labels (trigger + desktop info)
    const sizeVal = variant.options[this._data.sizeOptionIndex];
    if (sizeVal) {
      this._el.querySelectorAll('[data-sbb-size-value]').forEach((el) => {
        el.textContent = sizeVal;
      });
    }

    // Sync selected state on size buttons in the overlay
    this._el.querySelectorAll('[data-sbb-size-btn]').forEach((btn) => {
      const selected = variant.options[this._data.sizeOptionIndex] === btn.dataset.sizeName;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', String(selected));
    });
  }

  // ─── Overlay triggers ────────────────────────────────────────────────────────

  _initTriggers() {
    const sig = { signal: this._controller.signal };

    this._el.querySelectorAll('.sbb__trigger--color').forEach((btn) => {
      btn.addEventListener('click', () => this._toggleOverlay('color'), sig);
    });
    this._el.querySelectorAll('.sbb__trigger--size').forEach((btn) => {
      btn.addEventListener('click', () => this._toggleOverlay('size'), sig);
    });
  }

  _toggleOverlay(type) {
    const overlay = document.getElementById(`psatc-overlay-${type}-${this._productId}`);
    if (overlay?.classList.contains('sbb__overlay--open')) {
      this._closeOverlays();
    } else {
      this._openOverlay(type);
    }
  }

  _openOverlay(type) {
    this._closeOverlays();
    this._overlayOpen = true;

    const overlay = document.getElementById(`psatc-overlay-${type}-${this._productId}`);
    if (overlay) {
      overlay.classList.add('sbb__overlay--open');
      overlay.setAttribute('aria-hidden', 'false');
    }

    if (this._backdrop) {
      this._backdrop.classList.add('sbb__backdrop--visible');
      this._backdrop.removeAttribute('aria-hidden');
    }

    this._el.querySelectorAll(`.sbb__trigger--${type}`)
      .forEach((t) => t.setAttribute('aria-expanded', 'true'));

    document.body.style.overflow = 'hidden';
  }

  _closeOverlays() {
    this._overlayOpen = false;
    this._el.querySelectorAll('.sbb__overlay').forEach((overlay) => {
      overlay.classList.remove('sbb__overlay--open');
      overlay.setAttribute('aria-hidden', 'true');
    });

    if (this._backdrop) {
      this._backdrop.classList.remove('sbb__backdrop--visible');
      this._backdrop.setAttribute('aria-hidden', 'true');
    }

    this._el.querySelectorAll('.sbb__trigger')
      .forEach((t) => t.setAttribute('aria-expanded', 'false'));

    document.body.style.overflow = '';
  }

  // ─── Size buttons ─────────────────────────────────────────────────────────────

  _initSizeButtons() {
    this._el.querySelectorAll('[data-sbb-size-btn]').forEach((btn) => {
      // Sold-out sizes stay clickable here too (matches the main variant-picker
      // when "hide sold out variants" is off — see snippets/option-value.liquid)
      // — selecting one forwards to the hidden radio below, which triggers
      // <product-rerender> and swaps the ATC button into its Notify Me state
      // automatically, no extra wiring needed. If that option's real radio is
      // html-disabled (hide_sold_out_variants on), radio.click() below is a
      // native no-op, so no separate disabled-guard is needed here.
      btn.addEventListener(
        'click',
        () => {
          const sizeName = btn.dataset.sizeName;

          // Click the hidden radio in the main product form to trigger a
          // native variant:change — product-rerender then updates the button.
          if (this._formId) {
            const paramName = `${this._formId}-option${this._data.sizeOptionPosition}`;
            const radio = document.querySelector(
              `input[name="${paramName}"][value="${btn.dataset.optionValueId}"]`
            );
            if (radio && !radio.checked) radio.click();
          }

          // Optimistic UI: update all size labels and selection immediately
          this._el.querySelectorAll('[data-sbb-size-value]').forEach((el) => {
            el.textContent = sizeName;
          });
          this._el.querySelectorAll('[data-sbb-size-btn]').forEach((b) => {
            const sel = b === btn;
            b.classList.toggle('is-selected', sel);
            b.setAttribute('aria-pressed', String(sel));
          });

          // Brief pause so the user sees their selection register before close
          setTimeout(() => this._closeOverlays(), 180);
        },
        { signal: this._controller.signal }
      );
    });
  }

  // ─── Colour navigation — size persistence ────────────────────────────────────

  _initColorLinks() {
    const sig = { signal: this._controller.signal };

    const saveSize = () => {
      const sizeName = this._sizeValueEl?.textContent?.trim();
      try {
        if (sizeName) sessionStorage.setItem('sbb-selected-size', sizeName);
      } catch (e) { /* storage blocked (private mode) — size just isn't carried over */ }
    };

    // Save size when navigating via SBB colour overlay
    const colorOverlay = document.getElementById(`psatc-overlay-color-${this._productId}`);
    colorOverlay?.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', saveSize, sig);
    });

    // Also save size when navigating via main product variant-picker colour swatches
    // (combined products: each colour is a separate product URL)
    const variantPicker = document.querySelector(
      `variant-picker[handle="${this._data.productHandle}"]`
    );
    variantPicker?.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', saveSize, sig);
    });
  }

  _restoreSizeSelection() {
    let savedSize = null;
    try {
      savedSize = sessionStorage.getItem('sbb-selected-size');
      if (savedSize) sessionStorage.removeItem('sbb-selected-size');
    } catch (e) { /* storage blocked */ }
    if (!savedSize) return;

    requestAnimationFrame(() => {
      const btn = [...this._el.querySelectorAll('[data-sbb-size-btn]')].find(
        (b) => b.dataset.sizeName === savedSize && b.getAttribute('aria-disabled') !== 'true'
      );
      if (btn) btn.click();
    });
  }

  // ─── Close handlers ──────────────────────────────────────────────────────────

  _initClose() {
    const sig = { signal: this._controller.signal };
    this._backdrop?.addEventListener('click', () => this._closeOverlays(), sig);
    this._el.querySelectorAll('[data-sbb-close]').forEach((btn) => {
      btn.addEventListener('click', () => this._closeOverlays(), sig);
    });
  }

  // ─── Visibility observer ─────────────────────────────────────────────────────
  // SandwichVisibility (base theme) toggles .is-visible on the native element: on once the
  // product form has left the top of the screen, off again when the footer comes into view.
  // That is the only show/hide trigger, on mobile and desktop. When the bar hides, close any
  // open overlays so the backdrop is not left covering the page.
  // [CUSTOM] 2026-10-30: no longer desktop-only (mobile used its own dock before).

  _initVisibilityObserver() {
    this._visibilityObserver = new MutationObserver(() => {
      if (!this._el.classList.contains('is-visible')) this._closeOverlays();
    });
    this._visibilityObserver.observe(this._el, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────────────

  destroy() {
    this._controller?.abort();
    this._visibilityObserver?.disconnect();
    document.body.style.overflow = '';
  }
}

const init = (root = document) => {
  root.querySelectorAll('product-sticky-add-to-cart[data-product-id]').forEach((el) => {
    if (!el._sbb) el._sbb = new StickyBuyBarEnhancer(el);
  });
};

window.__sbbInit = init;

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

// Theme editor: sections are re-rendered without a page load.
document.addEventListener('shopify:section:load', (e) => init(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('product-sticky-add-to-cart').forEach((el) => {
    el._sbb?.destroy();
    delete el._sbb;
  });
});
})();
